import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  RefreshCw,
  Send,
  FileText,
  Users,
  Target,
  AlertTriangle,
  Layers,
  UserCheck,
  HelpCircle,
  Lightbulb,
  Check,
  Loader2,
  Trash2,
  CheckCircle2,
  Circle,
  XCircle,
  Brain,
  Database,
  Filter,
  FileCode,
  Sparkles,
  Code,
  Palette,
  Save,
  ExternalLink,
  Eye,
} from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useDigest, useDigestDates, useSendDigest, useDeleteDigest } from '@/hooks'
import { cn } from '@/lib/utils'
import type { Decision, Alert, Theme, PersonLoad, OpenItem, ActionItem } from '@/types'
import { useQueryClient } from '@tanstack/react-query'

// SSE Progress event interface
interface ProgressEvent {
  step: string
  message: string
  progress: number
  details?: string
  error?: string
  completed: boolean
  digest_id?: string
}

// Step configuration for visual display
// Batching pipeline steps
const GENERATION_STEPS = [
  { id: 'check', label: 'Verificando digest existente', icon: Database },
  { id: 'fetch', label: 'Buscando transcrições', icon: Database },
  { id: 'filter', label: 'Filtrando reuniões válidas', icon: Filter },
  { id: 'prepare', label: 'Organizando dados das reuniões', icon: FileCode },
  { id: 'batch_start', label: 'Iniciando processamento em lotes', icon: Layers },
  { id: 'batch_process', label: 'Processando lote...', icon: Sparkles },
  { id: 'consolidate', label: 'Consolidando resultados', icon: Brain },
  { id: 'ellen', label: 'Analisando com IA (modo direto)', icon: Brain },
  { id: 'parse', label: 'Processando resposta', icon: Code },
  { id: 'render', label: 'Renderizando HTML', icon: Palette },
  { id: 'save', label: 'Salvando digest', icon: Save },
]

// Steps that are conditionally shown based on pipeline mode
const BATCHING_STEPS = ['batch_start', 'batch_process', 'consolidate']
const DIRECT_STEPS = ['ellen']

// Error message translations
const ERROR_MESSAGES: Record<string, string> = {
  'NO_TRANSCRIPTS': 'Não há transcrições disponíveis para esta data. Verifique se houve reuniões gravadas.',
  'AI_ERROR': 'O serviço de IA está temporariamente indisponível. Tente novamente em alguns minutos.',
  'GENERATE_ERROR': 'Ocorreu um erro ao gerar o digest. Tente novamente.',
  'NOT_FOUND': 'Não há digest gerado para esta data.',
  'INVALID_DATE': 'Data inválida. Use o formato correto.',
  'FUTURE_DATE': 'Não é possível gerar digest para datas futuras.',
  'Request failed with status code 404': 'Não há digest gerado para esta data.',
  'Request failed with status code 503': 'Serviço temporariamente indisponível. Tente novamente.',
  'Request failed with status code 500': 'Erro interno do servidor. Tente novamente.',
  'Network Error': 'Erro de conexão. Verifique sua internet.',
}

function getErrorMessage(error: unknown): string {
  if (!error) return 'Erro desconhecido'

  const errorStr = error instanceof Error ? error.message : String(error)

  // Check for known error patterns
  for (const [pattern, message] of Object.entries(ERROR_MESSAGES)) {
    if (errorStr.includes(pattern)) {
      return message
    }
  }

  // If it contains "status code", try to make it friendlier
  if (errorStr.includes('status code')) {
    return 'Erro ao comunicar com o servidor. Tente novamente.'
  }

  return errorStr
}

// Tab definitions
const tabs = [
  { id: 'decisions', label: 'Decisões', icon: Target },
  { id: 'alerts', label: 'Alertas', icon: AlertTriangle },
  { id: 'themes', label: 'Eixos', icon: Layers },
  { id: 'people', label: 'Pessoas', icon: UserCheck },
  { id: 'open', label: 'Em Aberto', icon: HelpCircle },
  { id: 'actions', label: 'O que fazer', icon: Lightbulb },
] as const

type TabId = (typeof tabs)[number]['id']

export function DigestPage() {
  // Date navigation
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date()
    today.setDate(today.getDate() - 1) // Default to yesterday
    return today.toISOString().split('T')[0]
  })
  const [activeTab, setActiveTab] = useState<TabId>('decisions')
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [recipients, setRecipients] = useState('')

  // SSE Streaming state
  const [isGenerating, setIsGenerating] = useState(false)
  const [generationProgress, setGenerationProgress] = useState<ProgressEvent | null>(null)
  const [generationError, setGenerationError] = useState<string | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)

  const queryClient = useQueryClient()

  // Queries
  const { data: datesData } = useDigestDates()
  const { data: digest, isLoading, error } = useDigest(selectedDate)

  // Mutations (keeping send and delete, but generate is now SSE)
  const sendMutation = useSendDigest()
  const deleteMutation = useDeleteDigest()

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }, [])

  // Check for ongoing generation when date changes or on mount
  useEffect(() => {
    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }

    // Check if there's an ongoing generation for this date
    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('vcall_access_token')
        const baseUrl = import.meta.env.VITE_API_URL || '/api'
        const response = await fetch(`${baseUrl}/digest/status/${selectedDate}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.ok) {
          const state = await response.json()

          if (state.in_progress) {
            // There's an ongoing generation - reconnect to the stream
            setIsGenerating(true)
            setGenerationProgress({
              step: state.step,
              message: state.message,
              progress: state.progress,
              details: state.details,
              completed: false,
            })
            setGenerationError(null)

            // Start polling for updates (since we can't reconnect to SSE mid-stream)
            const pollInterval = setInterval(async () => {
              try {
                const pollResponse = await fetch(`${baseUrl}/digest/status/${selectedDate}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                })
                if (pollResponse.ok) {
                  const pollState = await pollResponse.json()

                  if (pollState.error) {
                    setGenerationError(pollState.error)
                    setIsGenerating(false)
                    clearInterval(pollInterval)
                    return
                  }

                  if (pollState.completed) {
                    setGenerationProgress({
                      step: 'complete',
                      message: 'Digest gerado com sucesso!',
                      progress: 100,
                      completed: true,
                      digest_id: pollState.digest_id,
                    })
                    setIsGenerating(false)
                    queryClient.invalidateQueries({ queryKey: ['digest-dates'] })
                    queryClient.invalidateQueries({ queryKey: ['digest', selectedDate] })
                    clearInterval(pollInterval)
                    return
                  }

                  if (pollState.in_progress) {
                    setGenerationProgress({
                      step: pollState.step,
                      message: pollState.message,
                      progress: pollState.progress,
                      details: pollState.details,
                      completed: false,
                    })
                  } else {
                    // Generation stopped without completing or error
                    clearInterval(pollInterval)
                    setIsGenerating(false)
                  }
                }
              } catch {
                // Ignore polling errors
              }
            }, 2000) // Poll every 2 seconds

            // Store cleanup function
            eventSourceRef.current = {
              close: () => clearInterval(pollInterval),
            } as EventSource
          } else if (state.completed && state.digest_id) {
            // Generation completed while we were away
            setGenerationProgress({
              step: 'complete',
              message: 'Digest gerado com sucesso!',
              progress: 100,
              completed: true,
              digest_id: state.digest_id,
            })
            setIsGenerating(false)
            setGenerationError(null)
          } else if (state.error) {
            // Generation failed while we were away
            setGenerationError(state.error)
            setIsGenerating(false)
            setGenerationProgress(null)
          } else {
            // No ongoing generation
            setGenerationProgress(null)
            setGenerationError(null)
            setIsGenerating(false)
          }
        } else {
          // No status available
          setGenerationProgress(null)
          setGenerationError(null)
          setIsGenerating(false)
        }
      } catch {
        // Error checking status - just reset state
        setGenerationProgress(null)
        setGenerationError(null)
        setIsGenerating(false)
      }
    }

    checkStatus()
  }, [selectedDate, queryClient])

  // Check if current date has digest
  const hasDigest = useMemo(() => {
    return datesData?.dates?.some((d) => d.date === selectedDate && d.has_digest)
  }, [datesData, selectedDate])

  // Date navigation helpers
  const navigateDate = (direction: 'prev' | 'next') => {
    const date = new Date(selectedDate + 'T12:00:00')
    date.setDate(date.getDate() + (direction === 'next' ? 1 : -1))
    const newDate = date.toISOString().split('T')[0]

    // Don't allow future dates
    const today = new Date().toISOString().split('T')[0]
    if (newDate <= today) {
      setSelectedDate(newDate)
    }
  }

  const formatDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00')
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

  // Handlers
  const handleGenerate = useCallback(() => {
    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    // Reset state
    setIsGenerating(true)
    setGenerationProgress(null)
    setGenerationError(null)

    // Build URL with auth token
    const token = localStorage.getItem('vcall_access_token')
    const baseUrl = import.meta.env.VITE_API_URL || '/api'
    // baseUrl already includes /v1
    const url = `${baseUrl}/digest/generate/stream?date=${selectedDate}&force=${hasDigest ? 'true' : 'false'}`

    // Create EventSource with auth header via fetch + ReadableStream
    // Note: EventSource doesn't support custom headers, so we use fetch with streaming
    const abortController = new AbortController()

    fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      },
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No reader available')
        }

        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          // Process complete SSE messages
          const lines = buffer.split('\n\n')
          buffer = lines.pop() || '' // Keep incomplete message in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6)) as ProgressEvent
                setGenerationProgress(data)

                if (data.error) {
                  setGenerationError(data.error)
                  setIsGenerating(false)
                  reader.cancel()
                  return
                }

                if (data.completed) {
                  setIsGenerating(false)
                  // Invalidate queries to refresh data
                  queryClient.invalidateQueries({ queryKey: ['digest-dates'] })
                  queryClient.invalidateQueries({ queryKey: ['digest', selectedDate] })
                  reader.cancel()
                  return
                }
              } catch {
                // Ignore parse errors
              }
            }
          }
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setGenerationError(getErrorMessage(err))
          setIsGenerating(false)
        }
      })

    // Store abort controller for cleanup
    eventSourceRef.current = {
      close: () => abortController.abort(),
    } as EventSource
  }, [selectedDate, hasDigest, queryClient])

  const handleSend = () => {
    const recipientList = recipients
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean)

    if (recipientList.length > 0) {
      sendMutation.mutate(
        { date: selectedDate, request: { recipients: recipientList } },
        {
          onSuccess: () => {
            setSendDialogOpen(false)
            setRecipients('')
          },
        }
      )
    }
  }

  const handleDelete = () => {
    if (confirm('Tem certeza que deseja excluir este digest?')) {
      deleteMutation.mutate(selectedDate)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Daily Intelligence Digest</h1>
          <p className="text-muted-foreground">
            Análise consolidada das reuniões por dia
          </p>
        </div>

        {/* Date Navigation */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigateDate('prev')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-2 px-4 py-2 border rounded-lg bg-card min-w-[280px] justify-center">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{formatDateDisplay(selectedDate)}</span>
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateDate('next')}
            disabled={selectedDate >= new Date().toISOString().split('T')[0]}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={handleGenerate}
          disabled={isGenerating}
          variant={hasDigest ? 'outline' : 'default'}
        >
          {isGenerating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {hasDigest ? 'Regenerar' : 'Gerar'} Digest
        </Button>

        {hasDigest && digest && (
          <>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('vcall_access_token')
                  const baseUrl = import.meta.env.VITE_API_URL || '/api'

                  const response = await fetch(`${baseUrl}/digest/${selectedDate}/html`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  })

                  if (!response.ok) throw new Error('Failed to fetch')

                  const html = await response.text()
                  const blob = new Blob([html], { type: 'text/html' })
                  const url = URL.createObjectURL(blob)
                  window.open(url, '_blank')
                  // Revoke after a delay to allow the new window to load
                  setTimeout(() => URL.revokeObjectURL(url), 1000)
                } catch (err) {
                  console.error('Failed to open HTML report:', err)
                }
              }}
            >
              <Eye className="h-4 w-4 mr-2" />
              Ver Relatório HTML
              <ExternalLink className="h-3 w-3 ml-2 opacity-50" />
            </Button>

            <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Send className="h-4 w-4 mr-2" />
                  Enviar para Rocket.Chat
                  {digest.sent_to_rocketchat && (
                    <Badge variant="secondary" className="ml-2">
                      <Check className="h-3 w-3 mr-1" />
                      Enviado
                    </Badge>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Enviar Digest para Rocket.Chat</DialogTitle>
                  <DialogDescription>
                    Informe os usernames que receberão o digest (separados por vírgula)
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="recipients">Destinatários</Label>
                    <Input
                      id="recipients"
                      placeholder="usuario1, usuario2, usuario3"
                      value={recipients}
                      onChange={(e) => setRecipients(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleSend} disabled={sendMutation.isPending || !recipients.trim()}>
                    {sendMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Enviar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button variant="ghost" size="icon" onClick={handleDelete} disabled={deleteMutation.isPending}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </>
        )}
      </div>

      {/* Generation Progress */}
      {(isGenerating || generationProgress) && (
        <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              {generationError ? (
                <XCircle className="h-5 w-5 text-destructive" />
              ) : generationProgress?.completed ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <Sparkles className="h-5 w-5 text-primary animate-pulse" />
              )}
              {generationError
                ? 'Erro na geração'
                : generationProgress?.completed
                ? 'Digest gerado com sucesso!'
                : 'Gerando Daily Intelligence Digest...'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progress Bar */}
            {!generationError && (
              <div className="space-y-2">
                <Progress value={generationProgress?.progress || 0} className="h-2" />
                <p className="text-sm text-muted-foreground text-right">
                  {generationProgress?.progress || 0}%
                </p>
              </div>
            )}

            {/* Steps - Dynamically filter based on pipeline mode */}
            <div className="grid gap-2">
              {GENERATION_STEPS
                .filter((step) => {
                  const currentStep = generationProgress?.step || ''
                  // Determine which pipeline is being used based on current/past steps
                  const isBatchingMode = BATCHING_STEPS.some(s =>
                    currentStep === s ||
                    GENERATION_STEPS.findIndex(gs => gs.id === s) < GENERATION_STEPS.findIndex(gs => gs.id === currentStep)
                  )
                  const isDirectMode = DIRECT_STEPS.some(s =>
                    currentStep === s ||
                    GENERATION_STEPS.findIndex(gs => gs.id === s) < GENERATION_STEPS.findIndex(gs => gs.id === currentStep)
                  )

                  // If we haven't reached either mode yet, show common steps only
                  if (!isBatchingMode && !isDirectMode) {
                    return !BATCHING_STEPS.includes(step.id) && !DIRECT_STEPS.includes(step.id)
                  }

                  // In batching mode, hide direct steps
                  if (isBatchingMode && DIRECT_STEPS.includes(step.id)) return false

                  // In direct mode, hide batching steps
                  if (isDirectMode && BATCHING_STEPS.includes(step.id)) return false

                  return true
                })
                .map((step) => {
                const currentStep = generationProgress?.step || ''
                const filteredSteps = GENERATION_STEPS.filter((s) => {
                  const isBatchingMode = BATCHING_STEPS.some(ms =>
                    currentStep === ms ||
                    GENERATION_STEPS.findIndex(gs => gs.id === ms) < GENERATION_STEPS.findIndex(gs => gs.id === currentStep)
                  )
                  const isDirectMode = DIRECT_STEPS.some(ss =>
                    currentStep === ss ||
                    GENERATION_STEPS.findIndex(gs => gs.id === ss) < GENERATION_STEPS.findIndex(gs => gs.id === currentStep)
                  )
                  if (isBatchingMode && DIRECT_STEPS.includes(s.id)) return false
                  if (isDirectMode && BATCHING_STEPS.includes(s.id)) return false
                  if (!isBatchingMode && !isDirectMode && (BATCHING_STEPS.includes(s.id) || DIRECT_STEPS.includes(s.id))) return false
                  return true
                })
                const stepIndex = filteredSteps.findIndex((s) => s.id === step.id)
                const currentIndex = filteredSteps.findIndex((s) => s.id === currentStep)
                const isCompleted = stepIndex < currentIndex || generationProgress?.completed
                const isActive = step.id === currentStep && !generationProgress?.completed
                const isPending = stepIndex > currentIndex && !generationProgress?.completed

                return (
                  <div
                    key={step.id}
                    className={cn(
                      'flex items-center gap-3 p-2 rounded-lg transition-all',
                      isActive && 'bg-primary/10 border border-primary/30',
                      isCompleted && 'opacity-60'
                    )}
                  >
                    <div className="shrink-0">
                      {isCompleted ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : isActive ? (
                        <Loader2 className="h-5 w-5 text-primary animate-spin" />
                      ) : (
                        <Circle className="h-5 w-5 text-muted-foreground/40" />
                      )}
                    </div>
                    <step.icon
                      className={cn(
                        'h-4 w-4',
                        isActive ? 'text-primary' : 'text-muted-foreground'
                      )}
                    />
                    <span
                      className={cn(
                        'text-sm',
                        isActive ? 'font-medium text-foreground' : 'text-muted-foreground',
                        isPending && 'opacity-50'
                      )}
                    >
                      {step.label}
                    </span>
                  </div>
                )
              })}
            </div>

            {/* Current step details */}
            {generationProgress?.message && !generationError && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium">{generationProgress.message}</p>
                {generationProgress.details && (
                  <p className="text-xs text-muted-foreground mt-1">{generationProgress.details}</p>
                )}
              </div>
            )}

            {/* Error message */}
            {generationError && (
              <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/30">
                <p className="text-sm text-destructive">{getErrorMessage(generationError)}</p>
              </div>
            )}

            {/* Clear progress button after completion or error */}
            {(generationProgress?.completed || generationError) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setGenerationProgress(null)
                  setGenerationError(null)
                }}
              >
                Fechar
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && !isGenerating && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-destructive">Erro ao carregar digest</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {getErrorMessage(error)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Digest State */}
      {!isLoading && !error && !digest && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">Nenhum digest para esta data</h3>
            <p className="text-muted-foreground mb-4">
              Clique em "Gerar Digest" para criar uma análise das reuniões do dia.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Digest Content */}
      {digest && (
        <>
          {/* Stats + Pulse */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Stats Cards */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Reuniões
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{digest.stats.total_reunioes}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Participantes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{digest.stats.participantes_unicos}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Decisões
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{digest.stats.total_decisoes}</div>
              </CardContent>
            </Card>
          </div>

          {/* Pulse do Dia */}
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-primary">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                </span>
                Pulso do Dia
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg leading-relaxed">{digest.pulse}</p>
            </CardContent>
          </Card>

          {/* Org Chart (if available) */}
          {digest.org_chart && digest.org_chart.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Organograma Inferido
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 md:grid-cols-2">
                  {digest.org_chart.map((entry, i) => (
                    <div key={i} className="flex items-baseline gap-2 text-sm">
                      <span className="font-mono text-xs text-primary uppercase">
                        {entry.cargo_inferido}
                      </span>
                      <span className="font-medium">{entry.nome}</span>
                      <span className="text-muted-foreground">— {entry.evidencia}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-muted-foreground">
                  ⚠ Cargos inferidos pelo comportamento observado. Validar com organograma oficial.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Tabs */}
          <div className="border-b">
            <nav className="flex gap-4 overflow-x-auto pb-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap',
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  )}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]">
            {activeTab === 'decisions' && <DecisionsTab decisions={digest.decisions} />}
            {activeTab === 'alerts' && <AlertsTab alerts={digest.alerts} />}
            {activeTab === 'themes' && <ThemesTab themes={digest.themes} />}
            {activeTab === 'people' && <PeopleTab people={digest.people_map} />}
            {activeTab === 'open' && <OpenItemsTab items={digest.open_items} />}
            {activeTab === 'actions' && <ActionsTab actions={digest.action_items} />}
          </div>
        </>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Tab Components
// ────────────────────────────────────────────────────────────

function DecisionsTab({ decisions }: { decisions: Decision[] }) {
  if (!decisions?.length) {
    return <EmptyState message="Nenhuma decisão registrada" />
  }

  const impactColors: Record<string, string> = {
    estrategica: 'bg-red-500/10 text-red-500 border-red-500/20',
    tatica: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    operacional: 'bg-green-500/10 text-green-500 border-green-500/20',
  }

  const impactLabels: Record<string, string> = {
    estrategica: 'Estratégica',
    tatica: 'Tática',
    operacional: 'Operacional',
  }

  return (
    <div className="space-y-3">
      {decisions.map((dec, i) => (
        <Card key={i}>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Badge className={cn('shrink-0', impactColors[dec.impacto] || impactColors.operacional)}>
                {impactLabels[dec.impacto] || dec.impacto}
              </Badge>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{dec.texto}</p>
                <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                  {dec.decidido_por && <span>{dec.decidido_por}</span>}
                  {dec.sala && <span>Sala: {dec.sala}</span>}
                </div>
                {dec.nota && <p className="mt-2 text-sm text-muted-foreground italic">{dec.nota}</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function AlertsTab({ alerts }: { alerts: Alert[] }) {
  if (!alerts?.length) {
    return <EmptyState message="Nenhum alerta registrado" />
  }

  const alertStyles: Record<string, { icon: string; color: string }> = {
    'cross-meeting': { icon: '🔗', color: 'border-l-purple-500' },
    'gargalo-pessoa': { icon: '⚡', color: 'border-l-orange-500' },
    'risco-seguranca': { icon: '🔴', color: 'border-l-red-500' },
    clima: { icon: '👤', color: 'border-l-cyan-500' },
    contradicao: { icon: '⚠️', color: 'border-l-yellow-500' },
  }

  return (
    <div className="space-y-3">
      {alerts.map((alert, i) => {
        const style = alertStyles[alert.tipo] || { icon: '⚠️', color: 'border-l-gray-500' }
        return (
          <Card key={i} className={cn('border-l-4', style.color)}>
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <span className="text-xl">{style.icon}</span>
                <div>
                  <p className="text-xs font-mono uppercase text-muted-foreground mb-1">
                    {alert.tipo.replace('-', ' ')}
                  </p>
                  <p className="font-medium">{alert.titulo}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{alert.detalhe}</p>
                  {alert.reunioes_envolvidas?.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {alert.reunioes_envolvidas.map((sala, j) => (
                        <Badge key={j} variant="outline" className="text-xs">
                          {sala}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

function ThemesTab({ themes }: { themes: Theme[] }) {
  if (!themes?.length) {
    return <EmptyState message="Nenhum eixo temático identificado" />
  }

  const themeIcons: Record<string, string> = {
    'inteligência artificial': '🧠',
    ia: '🧠',
    inovação: '🧠',
    integrações: '🔌',
    api: '🔌',
    financeiro: '💰',
    produto: '💰',
    operações: '🧾',
    comercial: '📊',
    pessoas: '👥',
    dados: '📈',
  }

  const getIcon = (name: string) => {
    const lower = name.toLowerCase()
    for (const [key, icon] of Object.entries(themeIcons)) {
      if (lower.includes(key)) return icon
    }
    return '📋'
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {themes.map((theme, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <span>{getIcon(theme.nome)}</span>
                {theme.nome}
              </CardTitle>
              <Badge variant="secondary">
                {theme.reunioes_count} {theme.reunioes_count === 1 ? 'reunião' : 'reuniões'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{theme.resumo}</p>
            {theme.salas?.length > 0 && (
              <div className="flex gap-1 mt-3 flex-wrap">
                {theme.salas.map((sala, j) => (
                  <Badge key={j} variant="outline" className="text-xs font-mono">
                    {sala}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function PeopleTab({ people }: { people: PersonLoad[] }) {
  if (!people?.length) {
    return <EmptyState message="Nenhum compromisso mapeado" />
  }

  const loadColors: Record<string, string> = {
    sobrecarga: 'text-red-500',
    alta: 'text-red-400',
    media: 'text-orange-500',
    normal: 'text-green-500',
  }

  const loadLabels: Record<string, string> = {
    sobrecarga: '■■■■■ Sobrecarga ⚠️',
    alta: '■■■■□ Alta carga',
    media: '■■■□□ Carga média',
    normal: '■■□□□ Normal',
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {people.map((person, i) => (
        <Card key={i}>
          <CardContent className="pt-4">
            <p className="font-semibold">{person.pessoa}</p>
            {person.cargo_inferido && (
              <p className="text-xs font-mono text-primary uppercase mt-1">{person.cargo_inferido}</p>
            )}
            <p className={cn('text-xs font-mono mt-2', loadColors[person.carga] || loadColors.normal)}>
              {loadLabels[person.carga] || person.carga}
            </p>
            {person.tarefas?.length > 0 && (
              <p className="text-sm text-muted-foreground mt-2">{person.tarefas.join(' · ')}</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function OpenItemsTab({ items }: { items: OpenItem[] }) {
  if (!items?.length) {
    return <EmptyState message="Nenhum ponto em aberto" />
  }

  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <Card key={i} className="border-l-4 border-l-yellow-500">
          <CardContent className="pt-4">
            <p className="font-medium">{item.texto}</p>
            <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
              {item.sala && <span>Sala: {item.sala}</span>}
              {item.impacto && <span>{item.impacto}</span>}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function ActionsTab({ actions }: { actions: ActionItem[] }) {
  if (!actions?.length) {
    return <EmptyState message="Nenhuma recomendação de ação" />
  }

  const categoryColors: Record<string, string> = {
    'urgente-seguranca': 'border-l-red-500 bg-red-500/5',
    'estrategico-recursos': 'border-l-orange-500 bg-orange-500/5',
    'estrategico-produto': 'border-l-purple-500 bg-purple-500/5',
    'pessoas-risco': 'border-l-cyan-500 bg-cyan-500/5',
    'oportunidade-processo': 'border-l-green-500 bg-green-500/5',
    'oportunidade-comercial': 'border-l-blue-500 bg-blue-500/5',
    'meta-governanca': 'border-l-yellow-500 bg-yellow-500/5',
    'oportunidade-quick-win': 'border-l-green-400 bg-green-400/5',
  }

  const categoryLabels: Record<string, string> = {
    'urgente-seguranca': 'Urgente · Segurança',
    'estrategico-recursos': 'Estratégico · Recursos',
    'estrategico-produto': 'Estratégico · Produto',
    'pessoas-risco': 'Pessoas · Risco',
    'oportunidade-processo': 'Oportunidade · Processos',
    'oportunidade-comercial': 'Oportunidade · Comercial',
    'meta-governanca': 'Meta · Governança',
    'oportunidade-quick-win': 'Oportunidade · Quick Win',
  }

  return (
    <div className="space-y-4">
      {actions.map((action, i) => (
        <Card key={i} className={cn('border-l-4', categoryColors[action.categoria] || 'border-l-gray-500')}>
          <CardContent className="pt-4">
            <Badge variant="outline" className="text-xs font-mono mb-2">
              {categoryLabels[action.categoria] || action.categoria}
            </Badge>
            <p className="font-semibold">{action.titulo}</p>
            <p className="mt-2 text-sm text-muted-foreground">{action.contexto}</p>
            {action.recomendacao && (
              <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm">
                  <span className="font-semibold">Recomendação:</span> {action.recomendacao}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <p className="text-muted-foreground">{message}</p>
      </CardContent>
    </Card>
  )
}
